import React, { useMemo, useEffect, useState } from "react";
//Import Breadcrumb
import { Container } from "reactstrap";
import "react-toastify/dist/ReactToastify.css";

import { Search } from "lucide-react";

import Paginate from "../../../components/shared/paginate";
import { toast } from "react-toastify";
import useDebounce from "../../../hooks/useDebounce";
import { FetchSms } from "../../../apis/Sms";

const Otp = () => {
  const [, setOtps] = useState([]);
  const [currentPage, setCurrentPage] = useState("1");
  const [totalPages, setTotalPages] = useState("1");
  const [filteredOtps, setFilteredOtps] = useState([]);
  const [keyword, setKeyword] = useState("");
  const debouncedKeyword = useDebounce(keyword, 400);

  const limit = "10";

  const fetchOtps = async () => {
    try {
      const response = await FetchSms("OTP", currentPage, limit, keyword);
      if (response.success) {
        setOtps(response?.data?.data?.sms);
        setFilteredOtps(response?.data?.sms);
        setTotalPages(response?.data?.totalPages);
      } else {
        toast.error("Failed to fetch otps. Please try again later.");
      }
    } catch (error) {
      toast.error("Failed to fetch otps. Please try again later.");
    }
  };

  const searchOtps = async () => {
    try {
      const response = await FetchSms("OTP", currentPage, limit, keyword);
      if (response.success) {
        setFilteredOtps(response?.data?.sms);
        setTotalPages(response?.data?.totalPages);
      } else {
        toast.error("Failed to search otps. Please try again later.");
      }
    } catch (error) {
      toast.error("Failed to search otps. Please try again later.");
    }
  };

  useEffect(() => {
    if (debouncedKeyword) {
      searchOtps();
    } else {
      fetchOtps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedKeyword]);

  useEffect(() => {
    if (debouncedKeyword) {
      setCurrentPage("1");
    }
  }, [debouncedKeyword]);

  const columns = useMemo(
    () => [
      "ID",
      "Mobile Number",
      "Content",
      "OTP",
      "Type",
      "Status",
      "Expiry Time",
    ],
    [],
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]" fluid>
          <div className="p-6 flex flex-col gap-6 ">
            <h1 className="text-2xl dark:text-[#c7d8eb]">OTP</h1>

            <div className="lg:flex flex-col lg:border lg:border-b-0 border-gray-400 dark:border-white">
              <div className="border-x border-t lg:border-none border-gray-500 dark:border-white rounded-[6px_6px_0px_0px] w-full flex flex-col lg:flex-row justify-between gap-4 p-4">
                <div className="relative border max-w-[400px] border-gray-500 dark:border-white rounded-md w-full">
                  <Search
                    size={16}
                    className="absolute left-2 bottom-1/2 translate-y-1/2"
                  />
                  <input
                    className="pl-8 pr-6 py-2 text-base rounded-md w-full dark:bg-darkBg2"
                    type="search"
                    placeholder="Search"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                {/* TABLE HEADER */}
                <div className="hidden lg:flex">
                  {columns.map((column, index) => (
                    <div
                      key={`column-${index}`}
                      className={`${
                        index === 0 ? "w-[16%]" : "w-[14%]"
                      } min-w-[140px] content-center border-y border-gray-500 dark:border-white break-words py-2 text-center font-semibold`}
                    >
                      {column}
                    </div>
                  ))}
                </div>

                {/* TABLE DATA */}
                {filteredOtps && filteredOtps.length > 0 ? (
                  filteredOtps.map((sms, index) => (
                    <React.Fragment key={sms._id}>
                      {/* DESKTOP */}
                      <div className="hidden lg:flex">
                        <div className="w-[16%] min-w-[140px] px-1 border-b border-gray-500 dark:border-white py-2 text-center break-words">
                          {sms._id}
                        </div>

                        <div className="w-[14%] min-w-[140px] px-1 border-b border-gray-500 dark:border-white py-2 text-center">
                          {sms.mobileNumber}
                        </div>

                        <div className="w-[14%] min-w-[140px] px-1 border-b border-gray-500 dark:border-white py-2 text-center break-words">
                          {sms.content}
                        </div>

                        <div className="w-[14%] min-w-[140px] px-1 border-b border-gray-500 dark:border-white py-2 text-center">
                          {sms.otp || "-"}
                        </div>

                        <div className="w-[14%] min-w-[140px] px-1 border-b border-gray-500 dark:border-white py-2 text-center">
                          {sms.type}
                        </div>

                        <div className="w-[14%] min-w-[140px] px-1 border-b border-gray-500 dark:border-white py-2 text-center">
                          {sms.status}
                        </div>

                        <div className="w-[14%] min-w-[140px] px-1 border-b border-gray-500 dark:border-white py-2 text-center">
                          {sms.expiryTime
                            ? new Date(sms.expiryTime)
                                .toISOString()
                                .slice(0, 19)
                                .replace("T", " ")
                            : "-"}
                        </div>
                      </div>

                      {/* MOBILE */}
                      <div className="block lg:hidden mb-6 border-x border-y border-gray-500 dark:border-white">
                        <div className="flex justify-between p-2 border-b border-gray-500">
                          <div className="font-bold">ID</div>
                          <div>{sms._id}</div>
                        </div>

                        <div className="flex justify-between p-2 border-b border-gray-500">
                          <div className="font-bold">Mobile Number</div>
                          <div>{sms.mobileNumber}</div>
                        </div>

                        <div className="flex justify-between p-2 border-b border-gray-500">
                          <div className="font-bold">Content</div>
                          <div>{sms.content}</div>
                        </div>

                        <div className="flex justify-between p-2 border-b border-gray-500">
                          <div className="font-bold">OTP</div>
                          <div>{sms.otp || "-"}</div>
                        </div>

                        <div className="flex justify-between p-2 border-b border-gray-500">
                          <div className="font-bold">Type</div>
                          <div>{sms.type}</div>
                        </div>

                        <div className="flex justify-between p-2 border-b border-gray-500">
                          <div className="font-bold">Status</div>
                          <div>{sms.status}</div>
                        </div>

                        <div className="flex justify-between p-2">
                          <div className="font-bold">Expiry Time</div>
                          <div>
                            {sms.expiryTime
                              ? new Date(sms.expiryTime)
                                  .toISOString()
                                  .slice(0, 19)
                                  .replace("T", " ")
                              : "-"}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  ))
                ) : (
                  <div className="w-full px-1 border-b border-gray-500 py-2 text-center">
                    No data found
                  </div>
                )}
              </div>
            </div>

            <Paginate
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
            />
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Otp;
